"use client";

/**
 * Dev Sessions at a glance (Gordon 2026-11-02: "round numbers off, with click
 * slide down more details, toggle close"). Four cards with rounded numbers;
 * a card is a button that slides its panel open under the grid, the same card
 * again (or another card, ✕, Escape) closes it — the Platform glance pattern.
 * Cost = time × SCB's average developer salary (lib/dev-rate.ts), in kronor.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { ChevronDown, Clock, Coins, ExternalLink, Keyboard, MousePointer2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FitNumber } from "@/components/ui/fit-number";
import type { DevGlance as DevGlanceData, DevGlanceGroup, DevGlanceTop } from "@/lib/dev-glance";
import { DEV_HOURLY_RATE_SEK, DEV_MONTHLY_SALARY_SEK, DEV_RATE_SOURCE } from "@/lib/dev-rate";

type Key = "hours" | "cost" | "keys" | "mouse";
const KNOWN_TYPES = ["ai", "manual", "review"];
const CARDS: { key: Key; icon: typeof Clock; accent?: boolean }[] = [
  { key: "hours", icon: Clock },
  { key: "cost", icon: Coins, accent: true },
  { key: "keys", icon: Keyboard },
  { key: "mouse", icon: MousePointer2 },
];

function useFmt() {
  const locale = useLocale();
  return useMemo(() => {
    const make = (o: Intl.NumberFormatOptions) => { try { return new Intl.NumberFormat(locale, o); } catch { return new Intl.NumberFormat("en", o); } };
    const n = make({ maximumFractionDigits: 0 });
    const compact = make({ notation: "compact", maximumFractionDigits: 1 });
    const kr = make({ style: "currency", currency: "SEK", maximumFractionDigits: 0 });
    const krCompact = make({ style: "currency", currency: "SEK", notation: "compact", maximumFractionDigits: 0 });
    let day: Intl.DateTimeFormat, month: Intl.DateTimeFormat;
    try { day = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }); } catch { day = new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric" }); }
    try { month = new Intl.DateTimeFormat(locale, { month: "short", year: "numeric", timeZone: "UTC" }); } catch { month = new Intl.DateTimeFormat("en", { month: "short", year: "numeric", timeZone: "UTC" }); }
    return {
      n: (v: number) => n.format(Math.round(v)),
      compact: (v: number) => compact.format(v),
      kr: (v: number) => kr.format(Math.round(v)),
      krCompact: (v: number) => krCompact.format(v),
      day: (ms: number | null) => (ms ? day.format(new Date(ms)) : "—"),
      month: (key: string) => month.format(new Date(`${key}-01T00:00:00Z`)),
    };
  }, [locale]);
}

export function DevGlance({ glance }: { glance: DevGlanceData }) {
  const t = useTranslations("devGlance");
  const tg = useTranslations("dashboardGlance");
  const fmt = useFmt();
  const uid = useId().replace(/:/g, "");
  const panelId = `dev-glance-panel-${uid}`;
  const titleId = `dev-glance-title-${uid}`;
  const [open, setOpen] = useState<Key | null>(null);
  const [shown, setShown] = useState<Key | null>(null); // kept while the panel slides shut
  const cardRefs = useRef<Partial<Record<Key, HTMLButtonElement | null>>>({});
  const panelRef = useRef<HTMLElement>(null);

  const toggle = (key: Key) => { const next = open === key ? null : key; setOpen(next); if (next) setShown(next); };
  const close = () => { const key = open; setOpen(null); if (key) cardRefs.current[key]?.focus(); };

  // A panel opening below the fold scrolls into view once it has slid open.
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      const top = panelRef.current?.getBoundingClientRect().top ?? 0;
      if (top > window.innerHeight - 140) window.scrollBy({ top: top - window.innerHeight / 3, behavior: "smooth" });
    }, 320);
    return () => window.clearTimeout(timer);
  }, [open]);

  const hm = (minutes: number) => minutes >= 60
    ? `${t("hoursShort", { n: Math.floor(minutes / 60) })} ${t("minutesShort", { n: Math.round(minutes % 60) })}`
    : t("minutesShort", { n: Math.round(minutes) });

  const value: Record<Key, string> = {
    hours: t("hoursShort", { n: fmt.n(glance.hours) }),
    cost: fmt.krCompact(glance.costEur), // exact sum is in the panel
    keys: fmt.compact(glance.keystrokes),
    mouse: fmt.compact(glance.mouse),
  };
  const sub: Record<Key, string> = {
    hours: t("sessionsCount", { count: glance.sessions }),
    cost: t("cards.costSub", { rate: fmt.n(DEV_HOURLY_RATE_SEK) }),
    keys: t("cards.keysSub"),
    mouse: t("cards.mouseSub"),
  };

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-1.5 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-semibold tabular-nums">{children}</span>
    </div>
  );
  const typeLabel = (g: DevGlanceGroup) => (KNOWN_TYPES.includes(g.key) ? t(`type.${g.key}`) : g.key);
  const Groups = ({ title, rows, show, label = g => g.key }: { title: string; rows: DevGlanceGroup[]; show: (g: DevGlanceGroup) => string; label?: (g: DevGlanceGroup) => string }) => rows.length ? (
    <div>
      <h4 className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</h4>
      {rows.map(g => <Row key={g.key} label={label(g)}>{show(g)}</Row>)}
    </div>
  ) : null;
  const Top = ({ rows }: { rows: DevGlanceTop[] }) => rows.length ? (
    <div>
      <h4 className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("topSessions")}</h4>
      {rows.map((r, i) => <Row key={i} label={`${fmt.day(r.startMs)} · ${r.description || t("noDescription")}`}>{fmt.n(r.count)}</Row>)}
    </div>
  ) : null;

  const body = (key: Key) => {
    if (key === "hours") return (
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <Row label={t("sessions")}>{fmt.n(glance.sessions)}</Row>
          <Row label={t("avgSession")}>{hm(glance.avgMinutes)}</Row>
          {glance.longest && <Row label={t("longest")}>{hm(glance.longest.minutes)} · {fmt.day(glance.longest.startMs)}</Row>}
          <Row label={t("period")}>{fmt.day(glance.firstMs)} – {fmt.day(glance.lastMs)}</Row>
        </div>
        <div className="space-y-4">
          <Groups title={t("byType")} rows={glance.byType} label={typeLabel} show={g => `${t("hoursShort", { n: fmt.n(g.hours) })} · ${t("sessionsCount", { count: g.sessions })}`} />
          <Groups title={t("byPerson")} rows={glance.byPerson} show={g => `${t("hoursShort", { n: fmt.n(g.hours) })} · ${t("sessionsCount", { count: g.sessions })}`} />
        </div>
      </div>
    );
    if (key === "cost") return (
      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-3">
          <Row label={t("total")}>{fmt.kr(glance.costEur)}</Row>
          <p className="text-sm leading-relaxed">{t("rateText", { rate: fmt.n(DEV_HOURLY_RATE_SEK), monthly: fmt.n(DEV_MONTHLY_SALARY_SEK) })}</p>
          <a href={DEV_RATE_SOURCE.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-2">
            {t("source")} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
          <Groups title={t("byType")} rows={glance.byType} label={typeLabel} show={g => fmt.kr(g.costEur)} />
        </div>
        <Groups title={t("byMonth")} rows={[...glance.byMonth].reverse().map(m => ({ ...m, key: fmt.month(m.key) }))} show={g => `${fmt.kr(g.costEur)} · ${t("hoursShort", { n: fmt.n(g.hours) })}`} />
      </div>
    );
    const perHour = key === "keys" ? glance.keysPerHour : glance.mousePerHour;
    const perSession = key === "keys" ? glance.keysPerSession : glance.mousePerSession;
    return (
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <Row label={t("perHour")}>{fmt.n(perHour)}</Row>
          <Row label={t("perSession")}>{fmt.n(perSession)}</Row>
        </div>
        <Top rows={key === "keys" ? glance.topKeys : glance.topMouse} />
      </div>
    );
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {CARDS.map(({ key, icon: Icon, accent }) => {
          const active = open === key;
          return (
            <button key={key} type="button" ref={el => { cardRefs.current[key] = el; }} data-bg="card"
              aria-expanded={active} aria-controls={panelId} onClick={() => toggle(key)}
              className={cn(
                "flex w-full min-w-0 flex-col border bg-card p-4 text-left text-card-foreground shadow-sm [container-type:inline-size]",
                "transition-all duration-200 hover:border-foreground/30 motion-reduce:transition-none",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                accent && "border-primary/40 bg-primary/5",
                active && "border-foreground/40 bg-foreground/5 ring-2 ring-foreground/30",
              )}>
              {/* Icon beside the label, the number on its own row and sized to fit
                  (Gordon 2026-11-03: the number was cut off next to the icon). */}
              <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                <Icon className={cn("h-3.5 w-3.5 shrink-0", accent && "text-primary")} aria-hidden="true" />
                <span className="truncate">{t(`cards.${key}`)}</span>
              </span>
              <FitNumber className="mt-1">{value[key]}</FitNumber>
              <span className="min-w-0">
                <span className="mt-1 block text-xs text-muted-foreground">{sub[key]}</span>
                <span className="mt-1 flex items-center gap-0.5 text-3xs text-muted-foreground">
                  <ChevronDown className={cn("h-3 w-3 transition-transform duration-200 motion-reduce:transition-none", active && "rotate-180")} aria-hidden="true" />
                  {active ? tg("hintClose") : tg("hintOpen")}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* The slide: grid rows 0fr → 1fr animates to the content's own height. */}
      <div className={cn("grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none", open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
        <div className="min-h-0 overflow-hidden" inert={!open}>
          <section ref={panelRef} id={panelId} aria-labelledby={titleId} data-bg="card"
            onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); close(); } }}
            className="mt-3 border bg-card p-4 text-card-foreground shadow-sm sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 id={titleId} className="text-base font-semibold text-foreground">{shown ? tg("panelTitle", { name: t(`cards.${shown}`) }) : ""}</h3>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={close} aria-label={tg("close")}>
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            {shown && <div key={shown} className="animate-in fade-in-0 duration-300 motion-reduce:animate-none">{body(shown)}</div>}
          </section>
        </div>
      </div>
    </div>
  );
}
